import { Button } from '@/components/ui/button';
import * as Field from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import * as RadioGroup from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { toaster } from '@/components/ui/toast';
import { userId } from '@state/auth';
import { createForm, setValue, valiForm } from '@modular-forms/solid';
import { useMutation } from '@tanstack/solid-query';
import { For } from 'solid-js';
import { useService } from 'solid-services';
import { queryClient } from 'src/App';
import { TOPICS, TOPIC_KEYS } from '@features/communityContent/topics';
import { Box } from 'styled-system/jsx';
import { grid } from 'styled-system/patterns';
import { useState } from '../state';
import { savePost, updatePost } from './api';
import { type PostForm, PostSchema } from './postSchema';

export default function (props: { onSuccess: () => void }) {
  const { editPost, setEditPost } = useService(useState)();
  const isEdit = () => !!editPost();

  const mutation = useMutation(() => ({
    mutationFn: (values: PostForm) => {
      const existing = editPost();
      if (existing) {
        return updatePost({ id: existing.id, values });
      }
      return savePost(values);
    },
    onSuccess: () => {
      toaster.create({
        title: isEdit() ? 'Post updated' : 'Post created',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['go-read-posts', userId()] });
      setEditPost(undefined);
      props.onSuccess();
    },
    onError: (error) => {
      toaster.create({
        title: 'Error saving post',
        description: error.message,
        type: 'error',
      })
    },
  }));

  const [postForm, { Form, Field: FormField }] = createForm<PostForm>({
    validate: valiForm(PostSchema),
    initialValues: {
      title: editPost()?.title ?? '',
      topic: editPost()?.topic ?? 'finance',
      content: editPost()?.content ?? '',
    }
  });

  const handleSubmit = (values: PostForm) => {
    mutation.mutate(values);
  };

  return (
    <Form onSubmit={handleSubmit} class={grid({ gap: '3', w: 'full' })}>
      <FormField name="title">
        {(field, props) => (
          <Field.Root invalid={!!field.error}>
            <Field.Label>Title</Field.Label>
            <Input
              {...props}
              type="text"
              placeholder="Give it a clear headline…"
              value={field.value ?? ''}
            />
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      <FormField name="topic">
        {(field) => (
          <Field.Root invalid={!!field.error}>
            <Field.Label>Topic</Field.Label>
            <RadioGroup.Root
              colorPalette="jade"
              value={field.value}
              onValueChange={(details) => setValue(postForm, 'topic', details.value as any)}
            >
              <For each={TOPIC_KEYS}>
                {(topic) => (
                  <RadioGroup.Item value={topic}>
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemControl />
                    <RadioGroup.ItemText display='flex' alignItems='center' gap='2'>
                      <Box w='8px' h='8px' borderRadius='full' style={{ background: TOPICS[topic].color }} />
                      {TOPICS[topic].label}
                    </RadioGroup.ItemText>
                  </RadioGroup.Item>
                )}
              </For>
            </RadioGroup.Root>
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      <FormField name="content">
        {(field, props) => (
          <Field.Root invalid={!!field.error}>
            <Field.Label>Post</Field.Label>
            <Field.HelperText mt='-1' mb='1'>
              Write your article — paste any links right in the text
            </Field.HelperText>
            <Textarea
              {...props}
              rows={10}
              placeholder="Write your post… (blank lines separate paragraphs; paste links right in the text)"
              value={field.value ?? ''}
            />
            <Field.ErrorText>{field.error}</Field.ErrorText>
          </Field.Root>
        )}
      </FormField>

      <Button
        type="submit"
        loading={mutation.isPending}
        colorPalette="jade"
        variant="solid"
        justifySelf="start"
      >
        {isEdit() ? 'Update Post' : 'Post to Go Read'}
      </Button>
    </Form>
  );
}
